'use client';

import { Book, Title } from 'types';
import { useGlobalState } from '@/state';
import { Box } from '@/components/layout/box';
import Button from '@/components/button';
import { backendFunction, removeBookFromStore, removeListingFromTitle } from '@/clients/firebase/client';
import { ActionType } from '@/state/types';
import AdminActionModal from '@/components/admin-action-modal';

interface AdminActionsClientProps {
  titleData?: Title;
  bookData?: Book & { sold?: boolean; };
}
export default function AdminActionsClient({ titleData, bookData }: AdminActionsClientProps) {
  const { state, dispatch } = useGlobalState();
  const isAdmin = state?.user?.data?.isAdmin;
  const isSeller = state?.user?.data?.uid === bookData?.seller_id;
  const sold = bookData?.sold;

  async function handleRemoveFromStore() {
    const removeFromStoreAction = async () => {
      return removeBookFromStore(bookData);
    };
    dispatch({
      type: ActionType.ADD_MODAL_DATA,
      payload: {
        component: (
          <AdminActionModal
            title='Remove Listing From Store'
            description='Are you sure you want to remove this book from the store?'
            successMessage='Removed.'
            failedMessage='Something went wrong.'
            functionToExecute={removeFromStoreAction}
            showBackendError={true}
          />
        ),
      },
    });
  }

  async function handleRemoveFromTitle() {
    const removeFromTitleAction = async () => {
      return removeListingFromTitle(bookData);
    };
    dispatch({
      type: ActionType.ADD_MODAL_DATA,
      payload: {
        component: (
          <AdminActionModal
            title='Remove Listing From Title'
            description='Are you sure you want to remove this listing from the title? This will remove the ISBN from the listing and decrease the score so that it will be replaced by another listing.'
            successMessage='Removed. You should see the selected listing update momentarily.'
            failedMessage='Something went wrong.'
            functionToExecute={removeFromTitleAction}
            showBackendError={true}
          />
        ),
      },
    });
  }

  async function handleRelistBook() {
    if (!sold) return;
    const relistBookAction = async () => {
      return backendFunction<void, { book_id: string; }>(
        'booksmanager-relistBook',
        { book_id: bookData?.id }
      );
    };
    dispatch({
      type: ActionType.ADD_MODAL_DATA,
      payload: {
        component: (
          <AdminActionModal
            title='Relist Book'
            description='This book will be added to your store and available for purchase.'
            successMessage='This book has been relisted. Please allow up to ten minutes for this to reflect on your bookstore.'
            failedMessage='Something went wrong. Please try again or reach out to our support team if the problem continues.'
            functionToExecute={relistBookAction}
          />
        ),
      },
    });
  }

  async function handleCreateTitleFromListing() {
    if (titleData) return;
    if (!bookData?.isbn?.trim()) {
      alert(
        'This book has no isbn. Add an isbn first, and then see if it still needs a title created.'
      );
    }
    const createNewTitleAction = async () => {
      return backendFunction<{ slug: string; }, { listing_id: string; }>(
        'titles-createNewTitleFromListing',
        { listing_id: bookData?.id }
      );
    };
    dispatch({
      type: ActionType.ADD_MODAL_DATA,
      payload: {
        component: (
          <AdminActionModal
            title='Create New Title From Listing'
            description={`This will create a new title and isbn from this listing using ISBN: ${bookData?.isbn}. IMPORTANT: Make sure you have first verified that this isbn is correct before proceeding. If not correct, update the isbn on this listing, then check whether it still needs a title created.`}
            successMessage='New title created successfully'
            failedMessage='Something went wrong'
            functionToExecute={createNewTitleAction}
            showBackendError={true}
          />
        ),
      },
    });
  }

  if (!isAdmin && !isSeller) return null;

  return (
    <Box>
      {(isAdmin && !sold) && (
        <Box mb={0.5}>
          <Button
            onPress={handleRemoveFromStore}
            size='xtra-small'
            style='outline'
            text='Remove From Store'
          />
        </Box>
      )}
      {(!!isAdmin && titleData) && (
        <Box mb={0.5}>
          <Button
            onPress={handleRemoveFromTitle}
            size='xtra-small'
            style='outline'
            text='Remove From Title'
          />
        </Box>
      )}
      {((isAdmin || isSeller) && sold) && (
        <Box mb={0.5}>
          <Button
            onPress={handleRelistBook}
            size='xtra-small'
            style='outline'
            text='Relist Book'
          />
        </Box>
      )}
      {(isAdmin && !titleData) && (
        <Box mb={0.5}>
          <Button
            onPress={handleCreateTitleFromListing}
            size='xtra-small'
            style='outline'
            text='Create Title From Listing'
          />
        </Box>
      )}
    </Box>
  );
}